"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.findBankDetailsIssue = exports.refineBicMatchesIban = exports.bicSchema = exports.ibanSchema = exports.formatIban = exports.isBicConsistentWithIban = exports.isValidBic = exports.isValidSepaIban = exports.normalizeBankIdentifier = exports.BIC_IBAN_COUNTRY_MISMATCH_MESSAGE = exports.BIC_INVALID_MESSAGE = exports.IBAN_INVALID_MESSAGE = void 0;
const zod_1 = require("zod");
/**
 * Length of an IBAN per SEPA country. The app only ever pays by SEPA credit
 * transfer, so a country outside this table is rejected outright rather than
 * validated against the wider IBAN registry.
 */
const SEPA_IBAN_LENGTH_BY_COUNTRY = {
    AD: 24,
    AT: 20,
    BE: 16,
    BG: 22,
    CH: 21,
    CY: 28,
    CZ: 24,
    DE: 22,
    DK: 18,
    EE: 20,
    ES: 24,
    FI: 18,
    FR: 27,
    GB: 22,
    GI: 23,
    GR: 27,
    HR: 21,
    HU: 28,
    IE: 22,
    IS: 26,
    IT: 27,
    LI: 21,
    LT: 20,
    LU: 20,
    LV: 21,
    MC: 27,
    MD: 24,
    ME: 22,
    MT: 31,
    NL: 18,
    NO: 15,
    PL: 28,
    PT: 25,
    RO: 24,
    SE: 24,
    SI: 19,
    SK: 24,
    SM: 27,
    VA: 22,
};
/** Shape of an IBAN: country code, 2 check digits, then the BBAN. */
const IBAN_PATTERN = /^[A-Z]{2}\d{2}[A-Z0-9]+$/;
/** Shape of a BIC: 4-letter bank, 2-letter country, 2 location chars, optional 3-char branch. */
const BIC_PATTERN = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/;
/** Modulus of the ISO 7064 checksum every valid IBAN satisfies. */
const IBAN_MODULUS = 97;
/** Code point of `A`, so that `A` maps to 10 and `Z` to 35 in the checksum. */
const LETTER_BASE = 'A'.charCodeAt(0) - 10;
/** Number of leading IBAN characters moved to the end before the checksum. */
const IBAN_REARRANGED_PREFIX_LENGTH = 4;
/** Number of characters in an IBAN display group. */
const IBAN_DISPLAY_GROUP_SIZE = 4;
/** i18n key surfaced by forms when the IBAN is not a valid SEPA IBAN. */
exports.IBAN_INVALID_MESSAGE = 'form.errors.invalid-iban';
/** i18n key surfaced by forms when the BIC is malformed. */
exports.BIC_INVALID_MESSAGE = 'form.errors.invalid-bic';
/** i18n key surfaced by forms when the BIC country differs from the IBAN country. */
exports.BIC_IBAN_COUNTRY_MISMATCH_MESSAGE = 'form.errors.bic-iban-country-mismatch';
/**
 * Strips every whitespace character (including non-breaking spaces) and
 * uppercases, yielding the canonical form stored in database.
 *
 * @param value - the raw user input
 * @returns the canonical IBAN or BIC
 */
const normalizeBankIdentifier = (value) => value.replace(/\s+/g, '').toUpperCase();
exports.normalizeBankIdentifier = normalizeBankIdentifier;
/**
 * Computes the ISO 7064 mod-97 remainder of an IBAN.
 *
 * @param iban - a normalized IBAN made of letters and digits
 * @returns the remainder, which is 1 for a valid IBAN
 */
const ibanRemainder = (iban) => {
    const rearranged = iban.slice(IBAN_REARRANGED_PREFIX_LENGTH) +
        iban.slice(0, IBAN_REARRANGED_PREFIX_LENGTH);
    let remainder = 0;
    for (const char of rearranged) {
        const digits = /\d/.test(char)
            ? char
            : String(char.charCodeAt(0) - LETTER_BASE);
        for (const digit of digits) {
            remainder = (remainder * 10 + Number(digit)) % IBAN_MODULUS;
        }
    }
    return remainder;
};
/**
 * Checks that a value is a valid IBAN of a SEPA country: known country, the
 * length that country uses, and a correct mod-97 checksum.
 *
 * The national RIB key (French BBAN check) is deliberately not verified.
 * The input may carry spaces or lowercase letters — it is normalized first.
 *
 * @param value - the raw or normalized IBAN
 * @returns `true` when the IBAN is a valid SEPA IBAN
 * @example
 * isValidSepaIban('FR14 2004 1010 0505 0001 3M02 606'); // true
 */
const isValidSepaIban = (value) => {
    const iban = (0, exports.normalizeBankIdentifier)(value);
    if (!IBAN_PATTERN.test(iban)) {
        return false;
    }
    const expectedLength = SEPA_IBAN_LENGTH_BY_COUNTRY[iban.slice(0, 2)];
    if (expectedLength === undefined || iban.length !== expectedLength) {
        return false;
    }
    return ibanRemainder(iban) === 1;
};
exports.isValidSepaIban = isValidSepaIban;
/**
 * Checks the structure of a BIC (8 or 11 characters).
 *
 * The input may carry spaces or lowercase letters — it is normalized first.
 *
 * @param value - the raw or normalized BIC
 * @returns `true` when the BIC is well-formed
 */
const isValidBic = (value) => BIC_PATTERN.test((0, exports.normalizeBankIdentifier)(value));
exports.isValidBic = isValidBic;
/**
 * Checks that a BIC belongs to the same country as an IBAN. Returns `true`
 * when either one is absent or malformed, so it only ever flags a real
 * mismatch and leaves malformed values to their own validators.
 *
 * @param params.iban - the raw or normalized IBAN
 * @param params.bic - the raw or normalized BIC, possibly empty
 * @returns `false` only when both are well-formed and their countries differ
 */
const isBicConsistentWithIban = (params) => {
    if (!params.iban || !params.bic) {
        return true;
    }
    if (!(0, exports.isValidBic)(params.bic) || !(0, exports.isValidSepaIban)(params.iban)) {
        return true;
    }
    const ibanCountry = (0, exports.normalizeBankIdentifier)(params.iban).slice(0, 2);
    const bicCountry = (0, exports.normalizeBankIdentifier)(params.bic).slice(4, 6);
    return ibanCountry === bicCountry;
};
exports.isBicConsistentWithIban = isBicConsistentWithIban;
/**
 * Groups an IBAN for display — `FR1420041010050500013M02606` reads
 * `FR14 2004 1010 0505 0001 3M02 606`.
 *
 * @param value - the raw or normalized IBAN
 * @returns the grouped, uppercased IBAN
 */
const formatIban = (value) => {
    const iban = (0, exports.normalizeBankIdentifier)(value);
    const groups = [];
    for (let i = 0; i < iban.length; i += IBAN_DISPLAY_GROUP_SIZE) {
        groups.push(iban.slice(i, i + IBAN_DISPLAY_GROUP_SIZE));
    }
    return groups.join(' ');
};
exports.formatIban = formatIban;
/**
 * Zod schema for a required SEPA IBAN. Used by the API payload contracts and
 * by the web forms, so both reject the same values.
 */
exports.ibanSchema = zod_1.z
    .string({ message: 'form.errors.required' })
    .min(1, { message: 'form.errors.required' })
    .refine(exports.isValidSepaIban, { message: exports.IBAN_INVALID_MESSAGE });
/**
 * Zod schema for an optional BIC: an empty string means "no BIC", anything
 * else must be well-formed.
 */
exports.bicSchema = zod_1.z
    .string()
    .refine((value) => value.trim() === '' || (0, exports.isValidBic)(value), {
    message: exports.BIC_INVALID_MESSAGE,
});
/**
 * Zod `superRefine` callback flagging a BIC whose country differs from the
 * IBAN's. Chain it on any form or payload schema that carries both `iban` and
 * `bic`; the issue lands on the `bic` field.
 *
 * @param data - the parsed object holding `iban` and `bic`
 * @param ctx - the Zod refinement context
 */
const refineBicMatchesIban = (data, ctx) => {
    if (!(0, exports.isBicConsistentWithIban)({ iban: data.iban, bic: data.bic })) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['bic'],
            message: exports.BIC_IBAN_COUNTRY_MISMATCH_MESSAGE,
        });
    }
};
exports.refineBicMatchesIban = refineBicMatchesIban;
/**
 * Tells why a stored IBAN/BIC pair cannot be used for a SEPA transfer. A
 * missing BIC is fine (a SEPA transfer only needs the IBAN); a BIC that is
 * present must be well-formed and from the IBAN country.
 *
 * @param bankDetails.iban - the stored IBAN, possibly absent
 * @param bankDetails.bic - the stored BIC, possibly absent
 * @returns the first issue found, or `null` when the pair is usable
 */
const findBankDetailsIssue = (bankDetails) => {
    if (!bankDetails.iban?.trim()) {
        return 'missing_iban';
    }
    if (!(0, exports.isValidSepaIban)(bankDetails.iban)) {
        return 'invalid_iban';
    }
    if (!bankDetails.bic?.trim()) {
        return null;
    }
    const isBicUsable = (0, exports.isValidBic)(bankDetails.bic) && (0, exports.isBicConsistentWithIban)(bankDetails);
    return isBicUsable ? null : 'invalid_bic';
};
exports.findBankDetailsIssue = findBankDetailsIssue;
